import React from 'react';
import { useNavigate } from 'react-router-dom';

import { BOT_URL } from '../../config/bot';
import { categoryState, countNotifications } from '../../domain/notifications/notifications';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  selectNotificationPreferences,
  selectNotificationsPending,
  selectNotificationsStatus,
} from '../../store/selectors/notificationSelectors';
import {
  fetchNotificationPreferences,
  setAllNotifications,
  setNotificationCategory,
  setNotificationType,
} from '../../store/thunks';
import { openTelegramLink } from '../../telegram/telegram';
import { TavernButton, TavernEmptyState, TavernLoading, TavernSwitch } from '../../ui/components';

import './NotificationsPage.css';

export function NotificationsPage() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const preferences = useAppSelector(selectNotificationPreferences);
  const status = useAppSelector(selectNotificationsStatus);
  const pending = useAppSelector(selectNotificationsPending);

  const hasUser = useAppSelector((s) => s.auth.user != null);
  React.useEffect(() => {
    if (hasUser) void dispatch(fetchNotificationPreferences());
  }, [dispatch, hasUser]);

  const count = React.useMemo(
    () => (preferences ? countNotifications(preferences.categories) : null),
    [preferences],
  );

  const header = (
    <div className="notifications-header">
      <button type="button" className="notifications-back" onClick={() => navigate('/profile')}>
        ← Назад
      </button>
      <h1 className="ttbPageTitle">
        <span>🔔</span>
        Оповещения
      </h1>
    </div>
  );

  if (!preferences || !count) {
    return (
      <div className="ttbPage notifications-page">
        {header}
        {status === 'error' ? (
          <TavernEmptyState
            icon="⚠️"
            title="Не удалось загрузить настройки"
            description="Проверьте соединение и попробуйте ещё раз."
            action={
              <TavernButton variant="primary" onClick={() => void dispatch(fetchNotificationPreferences())}>
                Повторить
              </TavernButton>
            }
          />
        ) : (
          <TavernLoading />
        )}
      </div>
    );
  }

  const anyEnabled = count.enabled > 0;
  const allBusy = pending.includes('all');

  return (
    <div className="ttbPage notifications-page">
      {header}

      <div className="ttbPageContent notifications-content">
        {!preferences.botConnected && (
          <div className="notifications-banner" role="status">
            <div className="notifications-banner-text">
              <strong>Бот ещё не запущен</strong>
              <span>Оповещения приходят в Telegram. Запустите бота, чтобы получать их.</span>
            </div>
            <TavernButton variant="primary" onClick={() => openTelegramLink(BOT_URL)}>
              Открыть бота
            </TavernButton>
          </div>
        )}

        <div className="notifications-summary">
          <div>
            <div className="notifications-summary-count">
              Включено {count.enabled} из {count.total}
            </div>
            <div className="notifications-summary-hint">Изменения сохраняются сразу.</div>
          </div>
          <button
            type="button"
            className="notifications-link-button"
            disabled={allBusy}
            onClick={() => void dispatch(setAllNotifications({ enabled: !anyEnabled }))}
          >
            {anyEnabled ? 'Выключить все' : 'Включить все'}
          </button>
        </div>

        {preferences.categories.map((category) => {
          const state = categoryState(category);
          return (
            <section key={category.category} className="notifications-section">
              <div className="notifications-section-head">
                <div className="notifications-section-copy">
                  <h2 className="notifications-section-title">{category.title}</h2>
                  <div className="notifications-section-description">{category.description}</div>
                </div>
                <TavernSwitch
                  checked={state === 'all'}
                  mixed={state === 'some'}
                  disabled={pending.includes(`category:${category.category}`) || allBusy}
                  ariaLabel={`${category.title}: все оповещения`}
                  onChange={(enabled) =>
                    void dispatch(setNotificationCategory({ category: category.category, enabled }))
                  }
                />
              </div>

              <ul className="notifications-list">
                {category.types.map((type) => (
                  <li key={type.type} className="notifications-row">
                    <div className="notifications-row-copy">
                      <div className="notifications-row-title">{type.title}</div>
                      <div className="notifications-row-description">{type.description}</div>
                    </div>
                    <TavernSwitch
                      checked={type.enabled}
                      disabled={pending.includes(`type:${type.type}`) || allBusy}
                      ariaLabel={type.title}
                      onChange={(enabled) =>
                        void dispatch(setNotificationType({ type: type.type, enabled }))
                      }
                    />
                  </li>
                ))}
              </ul>
            </section>
          );
        })}

        <p className="notifications-note">
          Служебные сообщения о поиске компании отключить нельзя — они приходят, когда вам
          находят игроков.
        </p>
      </div>
    </div>
  );
}
